import React from 'react';
import { Box, Button, Typography, Paper } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import { keyframes } from '@emotion/react';
import { authColors, company } from '../../config/branding';

interface BackendConnectionErrorPageProps {
  isRetrying?: boolean;
  onRetry?: () => void;
}

const BackendConnectionErrorPage: React.FC<BackendConnectionErrorPageProps> = ({
  isRetrying = false,
  onRetry,
}) => {
  const handleRefresh = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  const fadeIn = keyframes({ from: { opacity: 0 }, to: { opacity: 1 } });
  const fadeUp = keyframes({
    from: { opacity: 0, transform: 'translateY(8px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  });
  const pulse = keyframes({
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.5 },
  });

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: authColors.background,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          opacity: 0.06,
          backgroundImage:
            'repeating-linear-gradient(90deg, rgba(217,217,217,0.35) 0px, rgba(217,217,217,0.35) 1px, transparent 1px, transparent 20px), repeating-linear-gradient(0deg, rgba(217,217,217,0.35) 0px, rgba(217,217,217,0.35) 1px, transparent 1px, transparent 20px)',
        },
      }}
    >
      <Paper
        elevation={3}
        sx={{
          maxWidth: 600,
          width: '90%',
          textAlign: 'center',
          p: { xs: 4, md: 6 },
          backgroundColor: authColors.card,
          border: `1px solid ${authColors.border}`,
          borderRadius: 4,
          position: 'relative',
          animation: `${fadeIn} 500ms ease-out`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: 'rgba(239,68,68,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '3px solid rgba(239,68,68,0.3)',
              animation: isRetrying ? `${pulse} 2s ease-in-out infinite` : 'none',
            }}
          >
            <CloudOffIcon sx={{ fontSize: 56, color: '#ef5350' }} />
          </Box>
        </Box>

        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: authColors.text,
            mb: 2,
            animation: `${fadeUp} 600ms ease-out`,
          }}
        >
          Connexion au serveur impossible
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: authColors.muted,
            mb: 4,
            animation: `${fadeUp} 700ms ease-out`,
          }}
        >
          Impossible de se connecter au backend. Veuillez vérifier que le serveur est démarré et
          accessible.
        </Typography>

        <Paper
          sx={{
            bgcolor: 'rgba(239,68,68,0.15)',
            borderRadius: 3,
            p: 3,
            mb: 4,
            border: `1px solid rgba(239,68,68,0.4)`,
            animation: `${fadeUp} 800ms ease-out`,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: '#ef5350',
              fontWeight: 700,
              fontSize: '0.8rem',
              mb: 1,
              display: 'block',
              textTransform: 'uppercase',
              letterSpacing: '1px',
            }}
          >
            Backend API
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: authColors.text,
              fontWeight: 600,
              fontFamily: 'monospace',
              fontSize: '1rem',
              wordBreak: 'break-all',
            }}
          >
            {process.env.REACT_APP_API_URL || window.location.origin}
          </Typography>
        </Paper>

        <Button
          onClick={handleRefresh}
          disabled={isRetrying}
          variant="contained"
          size="large"
          startIcon={!isRetrying && <RefreshIcon />}
          sx={{
            width: '100%',
            maxWidth: 400,
            py: 1.5,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '1.05rem',
            borderRadius: '12px',
            backgroundColor: '#4a90e2',
            animation: `${fadeUp} 900ms ease-out`,
            mb: 3,
            '&:hover': {
              backgroundColor: '#357abd',
            },
            '&.Mui-disabled': {
              backgroundColor: 'rgba(74,144,226,0.5)',
              color: authColors.text,
            },
          }}
        >
          {isRetrying ? 'Reconnexion en cours...' : 'Réessayer'}
        </Button>

        <Typography
          variant="body2"
          sx={{
            color: authColors.muted,
            fontSize: '0.9rem',
            display: 'block',
            animation: `${fadeUp} 1000ms ease-out`,
          }}
        >
          Si le problème persiste, contactez l'administrateur système
        </Typography>

        <Typography
          variant="caption"
          sx={{
            color: authColors.muted,
            fontSize: '0.75rem',
            mt: 4,
            display: 'block',
            opacity: 0.5,
          }}
        >
          {company.name} • Template Application
        </Typography>
      </Paper>
    </Box>
  );
};

export default BackendConnectionErrorPage;
